import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../lib/api';
import {
  Users,
  Building2,
  Shield,
  Activity,
  TrendingUp,
  TrendingDown,
  Clock,
  AlertTriangle,
  CheckCircle,
} from 'lucide-react';

const STATUS_COLOR: Record<string, string> = {
  healthy: 'text-green-600 bg-green-600',
  warning: 'text-yellow-600 bg-yellow-600',
  down: 'text-red-600 bg-red-600',
};
const STATUS_LABEL: Record<string, string> = { healthy: 'Healthy', warning: 'Warning', down: 'Down' };

interface StatsCardProps {
  title: string;
  value: string | number;
  change?: {
    value: number;
    positive: boolean;
  };
  icon: React.ReactNode;
}

const StatsCard: React.FC<StatsCardProps> = ({ title, value, change, icon }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-6">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600">{title}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {change && (
          <div className={`flex items-center mt-1 text-sm ${
            change.positive ? 'text-green-600' : 'text-red-600'
          }`}>
            {change.positive ? (
              <TrendingUp className="w-4 h-4 mr-1" />
            ) : (
              <TrendingDown className="w-4 h-4 mr-1" />
            )}
            {Math.abs(change.value)}%
          </div>
        )}
      </div>
      <div className="p-3 bg-gray-50 rounded-lg">
        {icon}
      </div>
    </div>
  </div>
);

interface RecentActivityItem {
  id: string;
  action: string;
  user: string;
  timestamp: string;
  type: 'success' | 'warning' | 'error';
}

const RecentActivity: React.FC<{ activities: RecentActivityItem[] }> = ({ activities }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-6">
    <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
    <div className="space-y-4">
      {activities.map((activity) => (
        <div key={activity.id} className="flex items-center space-x-3">
          <div className={`p-2 rounded-full ${
            activity.type === 'success' ? 'bg-green-100' :
            activity.type === 'warning' ? 'bg-yellow-100' :
            'bg-red-100'
          }`}>
            <Activity className={`w-4 h-4 ${
              activity.type === 'success' ? 'text-green-600' :
              activity.type === 'warning' ? 'text-yellow-600' :
              'text-red-600'
            }`} />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">{activity.action}</p>
            <p className="text-sm text-gray-500">by {activity.user}</p>
          </div>
          <div className="text-sm text-gray-500 flex items-center">
            <Clock className="w-3 h-3 mr-1" />
            {activity.timestamp}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: analytics, isLoading: analyticsLoading } = useQuery({
    queryKey: ['analytics'],
    queryFn: () => adminAPI.getAnalytics(),
  });

  const { data: auditLogs } = useQuery({
    queryKey: ['audit-logs-recent'],
    queryFn: () => adminAPI.getAuditLogs({ limit: 5 }),
  });

  const { data: health } = useQuery({ queryKey: ['health'], queryFn: () => adminAPI.getHealth(), refetchInterval: 60000 });
  const { data: alertsData } = useQuery({ queryKey: ['security-alerts'], queryFn: () => adminAPI.getSecurityAlerts() });

  if (analyticsLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-gray-200 h-32 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const recentActivities: RecentActivityItem[] = auditLogs?.data?.map((log) => ({
    id: log.id,
    action: log.action,
    user: log.userEmail || log.userId || 'Unknown',
    timestamp: new Date(log.timestamp).toLocaleString(),
    type: log.action.includes('failed') || log.action.includes('error') ? 'error' : 'success'
  })) || [];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">Overview of your Auth-as-a-Service system</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Users"
          value={analytics?.totalUsers || 0}
          change={{ value: 12, positive: true }}
          icon={<Users className="w-6 h-6 text-blue-600" />}
        />
        <StatsCard
          title="Organizations"
          value={analytics?.totalOrganizations || 0}
          change={{ value: 8, positive: true }}
          icon={<Building2 className="w-6 h-6 text-green-600" />}
        />
        <StatsCard
          title="Active Sessions"
          value={analytics?.activeSessions || 0}
          icon={<Shield className="w-6 h-6 text-purple-600" />}
        />
        <StatsCard
          title="Failed Logins (24h)"
          value={analytics?.failedLogins24h || 0}
          icon={<AlertTriangle className="w-6 h-6 text-red-600" />}
        />
      </div>

      {/* Charts and Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Growth Chart Placeholder */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">User Growth</h3>
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded">
            <p className="text-gray-500">Chart will be implemented with a charting library</p>
          </div>
        </div>

        {/* Recent Activity */}
        <RecentActivity activities={recentActivities} />
      </div>

      {/* System Health */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">System Status</h3>
          <div className="space-y-3">
            {(health?.components || []).map((c) => {
              const colors = (STATUS_COLOR[c.status] || STATUS_COLOR.warning).split(' ');
              return (
                <div key={c.name} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{c.name}</span>
                  <span className={`flex items-center ${colors[0]}`} title={c.detail || ''}>
                    <div className={`w-2 h-2 ${colors[1]} rounded-full mr-2`}></div>
                    {STATUS_LABEL[c.status] || c.status}
                  </span>
                </div>
              );
            })}
            {!health && <p className="text-sm text-gray-400">Checking…</p>}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Security Alerts</h3>
          <div className="space-y-3">
            {(alertsData?.alerts || []).length === 0 && (
              <div className="flex items-center text-sm text-green-700">
                <CheckCircle className="w-4 h-4 mr-2" /> No active alerts
              </div>
            )}
            {(alertsData?.alerts || []).map((a, i) => (
              <div key={i} className={`p-3 rounded border ${a.severity === 'critical' ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'}`}>
                <p className={`text-sm font-medium ${a.severity === 'critical' ? 'text-red-800' : 'text-yellow-800'}`}>{a.title}</p>
                <p className={`text-xs ${a.severity === 'critical' ? 'text-red-600' : 'text-yellow-600'}`}>{a.detail}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="space-y-2">
            <button onClick={() => navigate('/users')} className="w-full text-left p-2 hover:bg-gray-50 rounded text-sm">View All Users</button>
            <button onClick={() => navigate('/organizations')} className="w-full text-left p-2 hover:bg-gray-50 rounded text-sm">Organizations</button>
            <button onClick={() => navigate('/settings')} className="w-full text-left p-2 hover:bg-gray-50 rounded text-sm">System Settings</button>
            <button onClick={() => navigate('/audit-logs')} className="w-full text-left p-2 hover:bg-gray-50 rounded text-sm">View Audit Logs</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
